import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// Firebase imports
import { auth, db } from "./firebase/config";

// Context Providers
import { AuthProvider } from "./contexts/authcontext";
import { GameProvider } from "./contexts/gamecontext";
// import { NotificationProvider } from "./contexts/notificationcontext";

// Layout Components
import Layout from "./components/layout/layout";
import ProtectedRoute from "./components/Auth/protected-route.js";
import AdminRoute from "./components/Auth/admin-route.js";

// Utility Functions
import checkEligibility from "./utils/check-eligibility";

// Styles
import "./styles/index.css";

// Page Components
const BlockedPage = lazy(() => import("./pages/blocked-page"));
const Dashboard = lazy(() => import("./pages/dashboard-page"));
const GameBoard = lazy(() => import("./pages/game-board-page"));
const GameResults = lazy(() => import("./pages/game-results-page"));
const Games = lazy(() => import("./pages/games-page"));
const HowToPlay = lazy(() => import("./pages/howtoplay-page"));
const LandingPage = lazy(() => import("./pages/landing-page"));
const Leaderboard = lazy(() => import("./pages/leaderboard-page"));
const Legal = lazy(() => import("./pages/legal-page"));
const Login = lazy(() => import("./pages/login-page"));
const PaymentCancel = lazy(() => import("./pages/payment-cancel-page"));
const PaymentSuccess = lazy(() => import("./pages/payment-success-page"));
const Payouts = lazy(() => import("./pages/payouts-page"));
const Profile = lazy(() => import("./pages/profile-page"));
const Register = lazy(() => import("./pages/register-page"));
const Tier = lazy(() => import("./pages/tiers-page"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/admin-dashboard"));
const AdminGames = lazy(() => import("./pages/admin/admin-games"));
const AdminPayouts = lazy(() => import("./pages/admin/admin-payouts"));
const AdminPayoutsSingle = lazy(() => import("./pages/admin/admin-payouts-single"));
const AdminReports = lazy(() => import("./pages/admin/admin-reports"));
const AdminTrivia = lazy(() => import("./pages/admin/admin-trivia"));
const AdminUsers = lazy(() => import("./pages/admin/admin-users"));
const AdminUsersSingle = lazy(() => import("./pages/admin/admin-users-single"));
const AdminAffiliates = lazy(() => import("./pages/admin/admin-affiliates"));
const ReferralReports = lazy(() => import("./pages/admin/admin-referral-reports"));
const TeamsFanbase = lazy(() => import("./pages/admin/admin-teams-fanbase"));

// Utility Functions

// ---- PWA Registration ----
const registerServiceWorker = () => {
  if ("serviceWorker" in navigator) {
    // Skip registration on localhost to avoid HMR/caching loops in development
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      console.log("ℹ️ Skipping Service Worker registration on localhost");
      // Proactively unregister any existing stale workers on localhost
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
          console.log("🧹 Unregistered stale Service Worker");
        }
      });
      return;
    }

    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((reg) => console.log("✅ Service Worker registered:", reg))
        .catch((err) => console.error("❌ SW registration failed:", err));
    });
  }
};

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    const runEligibilityCheck = async () => {
      const data = await checkEligibility();
      console.log("[Session Eligibility]", data);
    };

    runEligibilityCheck();
  }, []); // ← runs once per page load (new session)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          if (userDoc.exists()) {
            setUserData({ id: userDoc.id, ...userDoc.data() });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const routesConfig = [
    { path: "/", element: <LandingPage />, layout: true },
    {
      path: "/dashboard",
      element: <Dashboard />,
      layout: true,
      protected: true,
    },
    // { path: "/free-entry", element: <FreeEntry />, layout: true },
    {
      path: "/game/:boardId",
      element: <GameBoard />,
      layout: true,
      protected: true,
    },
    { path: "/game/:boardId/results", element: <GameResults />, layout: true },
    { path: "/games", element: <Games />, layout: true, protected: true },
    { path: "/home", element: <LandingPage />, layout: true },
    { path: "/how-to-play", element: <HowToPlay />, layout: true },
    {
      path: "/leaderboard",
      element: <Leaderboard />,
      layout: true,
      admin: true,
    },
    { path: "/legal/:page", element: <Legal />, layout: true },
    { path: "/login", element: <Login />, layout: true },
    {
      path: "/payment-cancel",
      element: <PaymentCancel />,
      layout: true,
      protected: true,
    },
    {
      path: "/payment-success",
      element: <PaymentSuccess />,
      layout: true,
      protected: true,
    },
    { path: "/payouts", element: <Payouts />, layout: true, protected: true },
    { path: "/profile", element: <Profile />, layout: true, protected: true },
    { path: "/register", element: <Register />, layout: true },
    { path: "/restricted", element: <BlockedPage />, layout: false },
    { path: "/tiers", element: <Tier />, layout: true, protected: true },

    // Admin routes
    { path: "/admin", element: <AdminDashboard />, layout: true, admin: true },
    {
      path: "/admin/affiliates",
      element: <AdminAffiliates />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/games",
      element: <AdminGames />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/payouts",
      element: <AdminPayouts />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/payouts/:payoutId",
      element: <AdminPayoutsSingle />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/referral-reports",
      element: <ReferralReports />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/reports",
      element: <AdminReports />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/teams-reports",
      element: <TeamsFanbase />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/trivia",
      element: <AdminTrivia />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/users",
      element: <AdminUsers />,
      layout: true,
      admin: true,
    },
    {
      path: "/admin/users/:userId",
      element: <AdminUsersSingle />,
      layout: true,
      admin: true,
    },
  ];

  return (
    <AuthProvider value={{ user, userData }}>
      <GameProvider>
        <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: { background: "#363636", color: "#fff" },
              success: { duration: 3000 },
              error: { duration: 4000 },
            }}
          />
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            }
          >
            <Routes>
              {routesConfig.map(
                ({ path, element, layout, protected: isProtected, admin }) => {
                  const wrappedElement = layout ? (
                    <Layout>{element}</Layout>
                  ) : (
                    element
                  );

                  if (admin)
                    return (
                      <Route
                        key={path}
                        path={path}
                        element={<AdminRoute>{wrappedElement}</AdminRoute>}
                      />
                    );

                  if (isProtected)
                    return (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <ProtectedRoute>{wrappedElement}</ProtectedRoute>
                        }
                      />
                    );

                  return (
                    <Route key={path} path={path} element={wrappedElement} />
                  );
                }
              )}

              {/* Catch-all */}
              <Route
                path="*"
                element={<Navigate to={userData ? "/dashboard" : "/"} replace />}
              />
            </Routes>
          </Suspense>
        </Router>
      </GameProvider>
    </AuthProvider>
  );
}

export default App;
